import { globalBackendClient as backendClient } from '@mimir/client-common/globals/backendClient';
import type {
  FolderDto,
  FolderPathSegmentDto,
  AncestryResponseDto,
  GetFolderContentQueryParams,
  GetFolderContentScrollQueryParams,
  GetFolderContentResponseDto,
  UpdateFolderAccessControlDto,
} from '@mimir/common/dto/folderModels';
import type { HttpQueryParameters, AdditionalParams } from '@mimir/common/util/apiClient';
import type { TaskPriority } from '@mimir/common/async/taskPriority';
import { GetFolderCommand } from '@mimir/common/api/folderCommands';
import { callApiCommand } from '@mimir/client-common/api/callApiCommand';
import {
  api_addItemToFolder,
  api_createFolder,
  api_deleteFolder, api_deleteFolderSafe,
  api_getFolderAncestry,
  api_getFolderContent,
  api_getFolderContentScroll,
  api_getItemAncestry,
  api_moveFolder,
  api_removeItemFromFolder,
  api_renameFolder,
  api_updateFolderAccessControl,
  api_updateFolderOwner,
  api_updateFolderSortKeyAndParent
} from '@mimir/client-common/api/folderApi';

export const getItemAncestry = async (itemId: string, priority?: TaskPriority): Promise<FolderPathSegmentDto[][]> =>
  api_getItemAncestry(backendClient, itemId, undefined, priority);

/************************ folders ************************/

export const getFolder = (folderId: string): Promise<FolderDto> =>
  callApiCommand(backendClient, new GetFolderCommand({ folderId }), undefined);

export const getFolderContent = (folderId: string, queryParams: GetFolderContentQueryParams): Promise<GetFolderContentResponseDto> =>
  api_getFolderContent(backendClient, folderId, queryParams);

export const getFolderContentScroll = (folderId: string, queryParams: GetFolderContentScrollQueryParams) =>
  api_getFolderContentScroll(backendClient, folderId, queryParams);

export const createFolder = async (folderName: string, parentFolderId: string, isHidden = false) =>
  api_createFolder(backendClient, folderName, parentFolderId, isHidden);

export const renameFolder = async (folderId: string, folderName: string) =>
  api_renameFolder(backendClient, folderId, folderName);

export const updateFolderSortKeyAndParent = async (folderId: string, sortKey: string | null, targetFolderId: string | undefined, queryParams: HttpQueryParameters = {}) =>
  api_updateFolderSortKeyAndParent(backendClient, { folderId, sortKey, targetFolderId }, queryParams);

export const moveFolder = async (folderId: string, targetFolderId: string, implicitVisibilityUpdate: boolean) => {
  const queryParams = { implicitVisibilityUpdate };
  return api_moveFolder(backendClient, folderId, targetFolderId, queryParams);
};


export const updateFolderAccessControl = async (folderId: string, accessControl: UpdateFolderAccessControlDto, queryParams: HttpQueryParameters = {}) =>
  api_updateFolderAccessControl(backendClient, folderId, { ...accessControl }, queryParams);

export const updateFolderOwner = async (folderId: string, newOwner: string, queryParams: HttpQueryParameters = {}) =>
  api_updateFolderOwner(backendClient, folderId, newOwner, queryParams);

export const deleteFolder = async (folderId: string) =>
  api_deleteFolder(backendClient, folderId);

export const deleteFolderSafe = async (folderId: string, options: AdditionalParams = {}) =>
  api_deleteFolderSafe(backendClient, folderId, options);

export const addItemToFolder = async (folderId: string, itemId: string, parentIdToRemove?: string) =>
  api_addItemToFolder(backendClient, folderId, itemId, parentIdToRemove);

export const removeItemFromFolder = async (folderId: string, itemId: string) =>
  api_removeItemFromFolder(backendClient, folderId, itemId);

export const getFolderAncestry = async (ids: string[], type: 'folder' | 'item'): Promise<AncestryResponseDto> =>
  api_getFolderAncestry(backendClient, ids, type);

